// frontend/src/App.js
// Main application container with Redux authentication and role-based routing
// This is the main React app shell, loading the current user and wiring up role-based routing (UPS vs Agency), protected dashboards, auth pages, and the shared header/layout.

import React, { useEffect } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// Global components
import Header from './components/Header';

// Auth pages
import Login from './features/auth/Login';
import ForgotPassword from './features/auth/ForgotPassword';
import ResetPassword from './features/auth/ResetPassword';

// UPS admin layout and pages
import UpsDashboard from './features/ups/UpsDashboard';
import OrganizedDriverList from './features/ups/OrganizedDriverList';
import UpsReservedDriverList from './features/ups/UpsReservedDriverList';
import BannedDriversList from './features/ups/BannedDriversList';
import AccountDashboard from './features/ups/AccountDashboard';
import ManageAgencies from './features/ups/ManageAgencies';

// Agency layout and pages
import AgencyDashboard from './features/agency/Dashboard';
import ActiveDriverList from './features/agency/ActiveDriverList';
import ReservedDriverList from './features/agency/ReservedDriverList';
import BannedDriverList from './features/agency/BannedDriverList';

// Auth actions
import { fetchCurrentUser } from './features/auth/authSlice';

// Layout for public/default routes
function DefaultLayout() {
  return (
    <div className="min-h-screen bg-gray-50">
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  );
}

function App() {
  const dispatch = useDispatch();
  const { userInfo, loading } = useSelector(state => state.auth);

  useEffect(() => {
    if (localStorage.getItem('accessToken')) {
      dispatch(fetchCurrentUser());
    }
  }, [dispatch]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading...
      </div>
    );
  }

  const isLoggedIn = Boolean(userInfo);
  const role = userInfo?.role;

  return (
    <Router>
      {/* Show global header only if logged in */}
      {isLoggedIn && <Header />}

      <Routes>
        {/* UPS Admin Dashboard and Routes */}
        <Route
          path="/ups-admin/*"
          element={
            isLoggedIn && ['ups_admin', 'ups_user'].includes(role)
              ? <UpsDashboard />
              : <Navigate to="/" replace />
          }
        >
          {/* Group all driver-list routes under /drivers */}
          <Route path="drivers">
            {/* GET /ups-admin/drivers */}
            <Route index           element={<OrganizedDriverList   />} />
            {/* GET /ups-admin/drivers/active */}
            <Route path="active"   element={<OrganizedDriverList   />} />
            {/* GET /ups-admin/drivers/reserved */}
            <Route path="reserved" element={<UpsReservedDriverList />} />
            {/* GET /ups-admin/drivers/banned */}
            <Route path="banned"   element={<BannedDriversList      />} />
            {/* Other UPS pages */}
          </Route>
          
          {/* Other UPS pages (siblings of “drivers”) */}
          <Route path="accounts"        element={<AccountDashboard />} />
          <Route path="manage-agencies" element={<ManageAgencies />} />
          <Route path="*" element={<Navigate to="/ups-admin/drivers" replace />} />
        </Route>

        {/* Agency Dashboard and Routes */}
        <Route
          path="/agency/*"
          element={
            isLoggedIn && role?.startsWith('agency')
              ? <AgencyDashboard />
              : <Navigate to="/" replace />
          }
        >
          <Route index element={<ActiveDriverList />} />
          <Route path="drivers" element={<ActiveDriverList />} />
          <Route path="reserved" element={<ReservedDriverList />} />
          <Route path="banned" element={<BannedDriverList />} />
          <Route path="*" element={<Navigate to="/agency/drivers" replace />} />
        </Route>

        {/* Public / Auth Routes */}
        <Route element={<DefaultLayout />}>
          <Route
            path="/login"
            element={!isLoggedIn ? <Login /> : <Navigate to="/" replace />}
          />
          <Route
            path="/forgot-password"
            element={!isLoggedIn ? <ForgotPassword /> : <Navigate to="/" replace />}
          />
          <Route
            path="/reset-password"
            element={!isLoggedIn ? <ResetPassword /> : <Navigate to="/" replace />}
          />

          {/* Root path: Redirect based on role */}
          <Route
            path="/"
            element={
              isLoggedIn
                ? (role?.startsWith('agency')
                    ? <Navigate to="/agency/drivers" replace />
                    : <Navigate to="/ups-admin/drivers" replace />)
                : <Navigate to="/login" replace />
            }
          />

          {/* Catch-all fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Router>
  );
}

export default App;
